/*
 * @Description: app actions
 */
import { ActionTree, ActionContext } from 'vuex'
import { RootState, useStore } from '@/store'
import { state, UserState } from './state'
import { Mutations } from './mutations'
import { UserMutationTypes } from './mutation-types'
import { UserActionTypes } from './action-types'
import { bindMFA, userInfoRequest, verifyMFA } from '@/apis/user'
import { removeToken, setToken } from '@/utils/cookies'
import { PermissionActionType } from '../permission/action-types'
import router, { resetRouter } from '@/router'
import { RouteRecordRaw } from 'vue-router'
import { UserModel } from '@/model/userModel'
import { RootObject } from '@/model/rootObject'

type AugmentedActionContext = {
  commit<K extends keyof Mutations>(
    key: K,
    payload: Parameters<Mutations[K]>[1],
  ): ReturnType<Mutations[K]>
} & Omit<ActionContext<UserState, RootState>, 'commit'>

export interface Actions {
  [UserActionTypes.ACTION_MFA_LOGIN](
    { commit }: AugmentedActionContext,
    mfaInfo: { temporaryToken: string, mfaCode: string }
  ): void
  [UserActionTypes.ACTION_BIND_MFA_LOGIN](
    { commit }: AugmentedActionContext,
    mfaInfo: { temporaryToken: string, mfaCode: string }
  ): void
  [UserActionTypes.ACTION_RESET_TOKEN](
    { commit }: AugmentedActionContext
  ): void
  [UserActionTypes.ACTION_GET_USER_INFO](
    { commit }: AugmentedActionContext
  ): void
  [UserActionTypes.ACTION_CHANGE_ROLES](
    { commit }: AugmentedActionContext, role: string
  ): void
  [UserActionTypes.ACTION_LOGIN_OUT](
    { commit }: AugmentedActionContext,
  ): void
}

export const actions: ActionTree<UserState, RootState> & Actions = {
  async [UserActionTypes.ACTION_MFA_LOGIN](
    { commit }: AugmentedActionContext,
    mfaInfo: { temporaryToken: string, mfaCode: string }
  ) {
    const temporaryToken = mfaInfo.temporaryToken
    const mfaCode = mfaInfo.mfaCode
    await verifyMFA(temporaryToken, { mfaCode: mfaCode }).then(async(res: RootObject<any> | null) => {
      if (res?.code === 200 && res.data) {
        setToken(res.data)
        commit(UserMutationTypes.SET_TOKEN, res.data)
      } else {
        throw res ? res.msg : 'Access to verifyMFA failed'
      }
    }).catch((err) => {
      throw err
    })
  },

  async [UserActionTypes.ACTION_BIND_MFA_LOGIN](
    { commit }: AugmentedActionContext,
    mfaInfo: { temporaryToken: string, mfaCode: string }
  ) {
    const temporaryToken = mfaInfo.temporaryToken
    const mfaCode = mfaInfo.mfaCode
    await bindMFA(temporaryToken, { mfaCode: mfaCode }).then(async(res: RootObject<any> | null) => {
      if (res?.code === 200 && res.data) {
        setToken(res.data)
        commit(UserMutationTypes.SET_TOKEN, res.data)
      } else {
        throw res ? res.msg : 'Access to bindMFA failed'
      }
    }).catch((err) => {
      throw err
    })
  },

  [UserActionTypes.ACTION_RESET_TOKEN](
    { commit }: AugmentedActionContext) {
    removeToken()
    commit(UserMutationTypes.SET_TOKEN, '')
    commit(UserMutationTypes.SET_ROLES, [])
  },

  async [UserActionTypes.ACTION_GET_USER_INFO](
    { commit }: AugmentedActionContext
  ) {
    if (state.token === '') {
      throw Error('token is undefined!')
    }
    await userInfoRequest().then((res: any) => {
      if (res?.code === 200 && res.data != null) {
        const roles = res.data.roles.split(',') || []
        const info: UserModel = res.data
        commit(UserMutationTypes.SET_ROLES, roles)
        commit(UserMutationTypes.SET_INFO, info)
        commit(UserMutationTypes.SET_MENU, res.data.menuList)
        commit(UserMutationTypes.SET_PERMISSION, res.data.permission)
        return res
      } else {
        throw Error('Verification failed, please Login again.')
      }
    }).catch(err => {
      throw Error(err?.msg)
    })
  },

  async [UserActionTypes.ACTION_CHANGE_ROLES](
    { commit }: AugmentedActionContext,
    role: string
  ) {
    const token = role + '-token'
    const store = useStore()
    commit(UserMutationTypes.SET_TOKEN, token)
    setToken(token)
    await store.dispatch(UserActionTypes.ACTION_GET_USER_INFO, undefined)
    store.dispatch(PermissionActionType.ACTION_SET_ROUTES, state.roles)
    store.state.permission.dynamicRoutes.forEach((item: RouteRecordRaw) => {
      router.addRoute(item)
    })
  },

  [UserActionTypes.ACTION_LOGIN_OUT](
    { commit }: AugmentedActionContext
  ) {
    removeToken()
    commit(UserMutationTypes.SET_TOKEN, '')
    commit(UserMutationTypes.SET_ROLES, [])
    resetRouter()
  }
}
